// 🧪 System Test Page - Verify All CloudFlare Integrations

import { useState, useEffect } from 'react';

interface TestResult {
  name: string;
  status: 'pending' | 'running' | 'pass' | 'fail' | 'warning';
  message: string;
  details?: any;
}

export function SystemTest() {
  const [tests, setTests] = useState<TestResult[]>([
    { name: 'Environment Variables', status: 'pending', message: '' },
    { name: 'API Health Check', status: 'pending', message: '' },
    { name: 'CloudFlare Bindings', status: 'pending', message: '' },
    { name: 'D1 Database (CRUD)', status: 'pending', message: '' },
    { name: 'KV Cache (Read/Write)', status: 'pending', message: '' },
    { name: 'R2 Storage', status: 'pending', message: '' },
    { name: 'AI Inference', status: 'pending', message: '' },
  ]);

  const apiUrl = import.meta.env.VITE_API_URL;
  const projectName = import.meta.env.VITE_PROJECT_NAME || 'FireStarter';

  useEffect(() => {
    runTests();
  }, []);

  const updateTest = (index: number, updates: Partial<TestResult>) => {
    setTests(prev => prev.map((test, i) =>
      i === index ? { ...test, ...updates } : test
    ));
  };

  const runTests = async () => {
    // Test 1: Environment Variables
    updateTest(0, { status: 'running', message: 'Checking environment configuration...' });
    await new Promise(resolve => setTimeout(resolve, 500));

    const hasApiUrl = !!apiUrl;

    if (hasApiUrl) {
      updateTest(0, {
        status: 'pass',
        message: 'Environment configured correctly',
        details: {
          VITE_API_URL: apiUrl,
          VITE_PROJECT_NAME: projectName,
          environment: import.meta.env.MODE || 'production',
        },
      });
    } else {
      updateTest(0, {
        status: 'fail',
        message: 'Missing VITE_API_URL in environment',
        details: { hasApiUrl },
      });
      return; // Stop if API URL missing
    }

    // Test 2: API Health Check
    updateTest(1, { status: 'running', message: 'Checking API connectivity...' });
    await new Promise(resolve => setTimeout(resolve, 300));

    try {
      const response = await fetch(`${apiUrl}/health`, {
        method: 'GET',
      });
      const data = await response.json();

      if (data.success) {
        updateTest(1, {
          status: 'pass',
          message: 'API is healthy and responding',
          details: data.data,
        });
      } else {
        updateTest(1, {
          status: 'fail',
          message: 'API returned unsuccessful response',
          details: data,
        });
      }
    } catch (error) {
      updateTest(1, {
        status: 'fail',
        message: 'Cannot reach API',
        details: {
          error: error instanceof Error ? error.message : 'Unknown error',
          apiUrl,
        },
      });
      return; // Stop if API unreachable
    }

    // Test 3: CloudFlare Bindings Status
    updateTest(2, { status: 'running', message: 'Checking CloudFlare bindings...' });
    await new Promise(resolve => setTimeout(resolve, 300));

    try {
      const response = await fetch(`${apiUrl}/bindings`);
      const data = await response.json();

      if (data.success) {
        const { db, cache, storage, ai } = data.data;
        const allBound = db && cache && storage && ai;

        updateTest(2, {
          status: allBound ? 'pass' : 'warning',
          message: allBound
            ? 'All CloudFlare bindings configured'
            : 'Some bindings are missing',
          details: {
            'D1 Database': db ? '✅' : '❌',
            'KV Cache': cache ? '✅' : '❌',
            'R2 Storage': storage ? '✅' : '❌',
            'AI Inference': ai ? '✅' : '❌',
          },
        });
      } else {
        updateTest(2, {
          status: 'fail',
          message: 'Failed to check bindings',
        });
      }
    } catch (error) {
      updateTest(2, {
        status: 'fail',
        message: 'Bindings check failed',
        details: { error: error instanceof Error ? error.message : 'Unknown error' },
      });
    }

    // Test 4: D1 Database CRUD
    updateTest(3, { status: 'running', message: 'Testing D1 database operations...' });
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
      const response = await fetch(`${apiUrl}/api/validate/db`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();

      if (data.success) {
        updateTest(3, {
          status: 'pass',
          message: 'D1 database CRUD operations successful',
          details: { operations: data.message },
        });
      } else {
        updateTest(3, {
          status: 'fail',
          message: 'D1 database test failed',
          details: { error: data.error },
        });
      }
    } catch (error) {
      updateTest(3, {
        status: 'fail',
        message: 'Database test error',
        details: { error: error instanceof Error ? error.message : 'Unknown error' },
      });
    }

    // Test 5: KV Cache
    updateTest(4, { status: 'running', message: 'Testing KV cache...' });
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
      const response = await fetch(`${apiUrl}/api/validate/cache`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: 'test-key',
          value: 'Hello from FireStarter!',
        }),
      });
      const data = await response.json();

      if (data.success) {
        updateTest(4, {
          status: 'pass',
          message: 'KV cache read/write successful',
          details: { operations: data.message },
        });
      } else {
        updateTest(4, {
          status: 'fail',
          message: 'KV cache test failed',
          details: { error: data.error },
        });
      }
    } catch (error) {
      updateTest(4, {
        status: 'fail',
        message: 'Cache test error',
        details: { error: error instanceof Error ? error.message : 'Unknown error' },
      });
    }

    // Test 6: R2 Storage
    updateTest(5, { status: 'running', message: 'Testing R2 storage...' });
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
      // Create a small test file
      const testFile = new Blob(['Test file content'], { type: 'text/plain' });
      const formData = new FormData();
      formData.append('file', testFile, 'test.txt');

      const response = await fetch(`${apiUrl}/api/upload`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();

      if (data.success) {
        updateTest(5, {
          status: 'pass',
          message: 'R2 storage upload successful',
          details: {
            key: data.data.key,
            name: data.data.name,
            size: data.data.size,
          },
        });
      } else {
        updateTest(5, {
          status: 'fail',
          message: 'R2 storage test failed',
          details: { error: data.error },
        });
      }
    } catch (error) {
      updateTest(5, {
        status: 'fail',
        message: 'Storage test error',
        details: { error: error instanceof Error ? error.message : 'Unknown error' },
      });
    }

    // Test 7: AI Inference
    updateTest(6, { status: 'running', message: 'Testing AI inference...' });
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
      const response = await fetch(`${apiUrl}/api/ai/text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: 'Say hello in one sentence.',
        }),
      });
      const data = await response.json();

      if (data.success) {
        updateTest(6, {
          status: 'pass',
          message: 'AI inference successful',
          details: {
            model: data.data.model || '@cf/meta/llama-3.1-8b-instruct',
            response: data.data.response,
          },
        });
      } else {
        updateTest(6, {
          status: 'fail',
          message: 'AI inference failed',
          details: { error: data.error },
        });
      }
    } catch (error) {
      updateTest(6, {
        status: 'fail',
        message: 'AI test error',
        details: { error: error instanceof Error ? error.message : 'Unknown error' },
      });
    }
  };

  const allPassed = tests.every(t => t.status === 'pass');
  const anyFailed = tests.some(t => t.status === 'fail');
  const anyWarning = tests.some(t => t.status === 'warning');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-4xl">🧪</span>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">System Test</h1>
                <p className="text-sm text-gray-600">Verify all CloudFlare integrations</p>
              </div>
            </div>
            <a
              href="#dashboard"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back to Dashboard
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Banner */}
        {allPassed && (
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-3">
              <span className="text-3xl">✅</span>
              <div>
                <h2 className="text-lg font-bold text-green-900">All Tests Passed!</h2>
                <p className="text-green-700">
                  Your {projectName} integration is fully configured and working.
                </p>
              </div>
            </div>
          </div>
        )}

        {anyFailed && !allPassed && (
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-3">
              <span className="text-3xl">❌</span>
              <div>
                <h2 className="text-lg font-bold text-red-900">Some Tests Failed</h2>
                <p className="text-red-700">
                  Check the failed tests below and fix the configuration.
                </p>
              </div>
            </div>
          </div>
        )}

        {anyWarning && !anyFailed && !allPassed && (
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-3">
              <span className="text-3xl">⚠️</span>
              <div>
                <h2 className="text-lg font-bold text-yellow-900">Some Bindings Missing</h2>
                <p className="text-yellow-700">
                  Some CloudFlare bindings are not configured yet.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Test Results */}
        <div className="space-y-4">
          {tests.map((test, index) => (
            <div
              key={index}
              className={`bg-white rounded-lg shadow-sm border-2 p-6 ${
                test.status === 'pass'
                  ? 'border-green-200'
                  : test.status === 'fail'
                  ? 'border-red-200'
                  : test.status === 'warning'
                  ? 'border-yellow-200'
                  : test.status === 'running'
                  ? 'border-blue-200'
                  : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">
                      {test.status === 'pass'
                        ? '✅'
                        : test.status === 'fail'
                        ? '❌'
                        : test.status === 'warning'
                        ? '⚠️'
                        : test.status === 'running'
                        ? '⏳'
                        : '⏸️'}
                    </span>
                    <h3 className="text-lg font-semibold text-gray-900">{test.name}</h3>
                  </div>
                  <p
                    className={`text-sm mb-3 ${
                      test.status === 'pass'
                        ? 'text-green-700'
                        : test.status === 'fail'
                        ? 'text-red-700'
                        : test.status === 'warning'
                        ? 'text-yellow-700'
                        : 'text-gray-600'
                    }`}
                  >
                    {test.message}
                  </p>

                  {test.details && (
                    <details className="text-sm">
                      <summary className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium">
                        View Details
                      </summary>
                      <pre className="mt-3 p-4 bg-gray-50 rounded border border-gray-200 overflow-x-auto">
                        {JSON.stringify(test.details, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="mt-8 flex gap-4">
          <button
            onClick={runTests}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            🔄 Re-run Tests
          </button>
          <a
            href="#dev"
            className="px-6 py-3 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 font-medium"
          >
            🛠️ Developer Hub
          </a>
        </div>
      </main>
    </div>
  );
}
